import { IPA_PHONEMES } from "@ai-teach/utils";
import { IoClose } from "react-icons/io5";

/**
 * Picks the sounds a phoneme is scored on. Order matters — an r-controlled vowel is the vowel then
 * the /ɹ/ — so selections append to a list rather than toggling in a set.
 *
 * Deliberately not a text field: /ɡ/ and /ɹ/ are U+0261 and U+0279, indistinguishable on screen
 * from ASCII "g" and "r", and a mistyped one matches nothing Azure emits.
 */
export function TargetIpaPicker({
  value,
  onChange,
  idPrefix,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  return (
    <div>
      <label htmlFor={`${idPrefix}-ipa`} className="block label mb-1">
        Target sound(s)
      </label>

      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {value.map((symbol, index) => (
            <span
              key={symbol}
              className="inline-flex items-center gap-1 rounded bg-surface border border-gray-800 px-2 py-1 text-sm text-secondary-200"
            >
              <span>{IPA_PHONEMES.find((p) => p.symbol === symbol)?.friendly ?? symbol}</span>
              <span className="font-mono text-secondary-400">{symbol}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                className="text-secondary-400 hover:text-red-400"
                aria-label={`Remove ${symbol}`}
              >
                <IoClose size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <select
        id={`${idPrefix}-ipa`}
        value=""
        onChange={(e) => {
          if (e.target.value) onChange([...value, e.target.value]);
        }}
        className="mt-1 block w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="">{value.length ? "Add another sound…" : "Choose a sound…"}</option>
        {IPA_PHONEMES.filter((phoneme) => !value.includes(phoneme.symbol)).map((phoneme) => (
          <option key={phoneme.symbol} value={phoneme.symbol}>
            {phoneme.friendly} — as in {phoneme.example} ({phoneme.symbol})
          </option>
        ))}
      </select>

      <p className="mt-1 text-xs text-secondary-400">
        Usually one sound. Add a second for an r-controlled vowel — "ar" is the vowel, then the r.
      </p>
    </div>
  );
}
